import { BASE_URL } from '@/lib/axios';

// Reimbursement claims (API: /expenses). Shapes mirror the API's ExpenseClaimDto.

export type ExpenseCategory = 'TRAVEL' | 'FUEL' | 'FOOD' | 'LODGING' | 'PHONE_INTERNET' | 'OTHER';

export type ExpenseStatus =
  | 'PENDING_MANAGER'
  | 'PENDING_HR'
  | 'PENDING_OWNER'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'PAID';

/** `ACTION_REQUIRED` is a list filter only: the claims the caller can act on right now. */
export type ExpenseStatusFilter = ExpenseStatus | 'ACTION_REQUIRED';

export interface ExpensePerson {
  id: string;
  name: string;
}

export interface ExpenseReview {
  by: ExpensePerson;
  at: string;
  remarks: string | null;
}

export type ExpenseStage = 'MANAGER' | 'HR' | 'OWNER';

export interface ExpenseRejection extends ExpenseReview {
  stage: ExpenseStage;
}

export interface ExpenseReceipt {
  id: string;
  mimeType: string;
  sizeBytes: number;
  /** Signed, short-lived (30 min). May be a path — resolve with `receiptSrc()`. */
  url: string;
}

export interface ExpenseClaim {
  id: string;
  clientId: string | null;
  category: ExpenseCategory;
  amount: number;
  expenseDate: string; // YYYY-MM-DD
  description: string;
  status: ExpenseStatus;
  createdAt: string;
  employee: {
    id: string;
    name: string;
    employeeCode: string;
    designation: string | null;
    department: string | null;
    avatarUrl: string | null;
  };
  /** Whose approval the manager step needs; null when the claim has no manager step. */
  managerReviewer: ExpensePerson | null;
  managerReview: ExpenseReview | null;
  hrReview: ExpenseReview | null;
  ownerReview: ExpenseReview | null;
  rejection: ExpenseRejection | null;
  payroll: { runId: string; month: number; year: number; status: string } | null;
  paidAt: string | null;
  receipts: ExpenseReceipt[];
  canApprove: boolean;
  canReject: boolean;
  canCancel: boolean;
}

export interface ReviewerExpenseSummary {
  actionRequired: number;
  pendingManager: number;
  pendingHr: number;
  pendingOwner: number;
  approvedUnpaid: number;
  paidThisMonth: number;
}

export interface MyExpenseSummary {
  pending: number;
  approved: number;
  paid: number;
  rejected: number;
}

export type ExpenseSummary = ReviewerExpenseSummary | MyExpenseSummary;

export function isReviewerSummary(s: ExpenseSummary | undefined): s is ReviewerExpenseSummary {
  return !!s && 'actionRequired' in s;
}

/** Roles that see every claim (HR step and Owner step). Managers get only their reports' claims. */
export const EXPENSE_REVIEWER_ROLES = ['SUPER_ADMIN', 'ORG_ADMIN', 'HR'] as const;

export function isExpenseReviewer(role: string | null | undefined): boolean {
  return !!role && (EXPENSE_REVIEWER_ROLES as readonly string[]).includes(role);
}

export const EXPENSE_CATEGORIES: { value: ExpenseCategory; label: string }[] = [
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'FUEL', label: 'Fuel' },
  { value: 'FOOD', label: 'Food' },
  { value: 'LODGING', label: 'Lodging' },
  { value: 'PHONE_INTERNET', label: 'Phone & Internet' },
  { value: 'OTHER', label: 'Other' },
];

export function categoryLabel(c: string): string {
  return EXPENSE_CATEGORIES.find((x) => x.value === c)?.label ?? c;
}

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export function monthYearShort(month: number, year: number): string {
  return `${MONTHS_SHORT[month - 1] ?? String(month)} ${String(year)}`;
}

/** Plain-English status, e.g. "Paid in Nov 2026 payslip". */
export function statusLabel(claim: Pick<ExpenseClaim, 'status' | 'payroll'>): string {
  switch (claim.status) {
    case 'PENDING_MANAGER':
      return 'Pending Manager';
    case 'PENDING_HR':
      return 'Pending HR';
    case 'PENDING_OWNER':
      return 'Pending Owner';
    case 'APPROVED':
      return claim.payroll
        ? `Approved – in ${monthYearShort(claim.payroll.month, claim.payroll.year)} payroll`
        : 'Approved – paid with next salary';
    case 'PAID':
      return claim.payroll
        ? `Paid in ${monthYearShort(claim.payroll.month, claim.payroll.year)} payslip`
        : 'Paid';
    case 'REJECTED':
      return 'Rejected';
    case 'CANCELLED':
      return 'Cancelled';
    default:
      return claim.status;
  }
}

export const STATUS_CLASS: Record<ExpenseStatus, string> = {
  PENDING_MANAGER: 'border-transparent bg-action-amber-bg text-action-amber-fg',
  PENDING_HR: 'border-transparent bg-action-amber-bg text-action-amber-fg',
  PENDING_OWNER: 'border-transparent bg-action-violet-bg text-action-violet-fg',
  APPROVED: 'border-transparent bg-action-blue-bg text-action-blue-fg',
  PAID: 'border-transparent bg-action-green-bg text-action-green-fg',
  REJECTED: 'border-transparent bg-action-rose-bg text-action-rose-fg',
  CANCELLED: 'border-transparent bg-muted text-muted-foreground',
};

export function formatInr(amount: number, opts: { decimals?: boolean } = {}): string {
  const hasPaise = Math.round(amount * 100) % 100 !== 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: opts.decimals || hasPaise ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** "2026-10-27" → "27 Oct 2026" without any timezone shift. */
export function formatDateOnly(ymd: string): string {
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return ymd;
  return `${String(d).padStart(2, '0')} ${MONTHS_SHORT[m - 1] ?? ''} ${String(y)}`;
}

/**
 * Receipt URLs are signed paths served by the API (no auth header needed).
 * Accepts an absolute URL, an `/api/v1/...` path, or a path relative to `/api/v1`.
 */
export function receiptSrc(url: string): string {
  if (/^https?:\/\//i.test(url) || url.startsWith('blob:') || url.startsWith('data:')) return url;
  const path = url.startsWith('/') ? url : `/${url}`;
  return path.startsWith('/api/') ? `${BASE_URL}${path}` : `${BASE_URL}/api/v1${path}`;
}

/** Images the browser can show inline. HEIC/HEIF (iPhone photos) don't render in Chrome/Edge → open/download tile. */
export function isImageMime(mime: string): boolean {
  return mime.startsWith('image/') && !isHeicMime(mime);
}

export function isHeicMime(mime: string): boolean {
  return mime === 'image/heic' || mime === 'image/heif';
}

export function isPdfMime(mime: string): boolean {
  return mime === 'application/pdf';
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${String(n)} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export const MAX_RECEIPTS = 5;
export const MAX_RECEIPT_BYTES = 8 * 1024 * 1024;
export const RECEIPT_ACCEPT =
  'image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif,application/pdf';
export const MAX_CLAIM_AGE_DAYS = 90;
export const MAX_CLAIM_AMOUNT = 100000;

/** RFC 4122 v4 id for idempotent submits (randomUUID needs a secure context). */
export function newClientId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    return crypto.randomUUID();
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x40;
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
